import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import SuperAdminSidebar from "./components/SuperAdminSidebar/SuperAdminSidebar";
import AdminSubHeader from "../../../components/AdminSubHeader/AdminSubHeader";
import SchoolView from "./components/SchoolView";
import Header from "../../../components/Layout/Header";
import ClassRoomView from "./components/ClassRoomView";
import StudentView from "./components/StudentView";
import { useSchoolHierarchy } from "../../../hooks/useSchoolHierarchy";
import { useCurrentUser } from "../../../hooks/useGetUserDetail";
import Activities from "../../Activities/Activities";
import TrooStories from "../../TrooStories/TrooStories";

const SuperAdmin = () => {
  const { tab: tabSlug } = useParams<{ tab?: string }>();
  const tab = tabSlug ? tabSlug.toUpperCase().replace(/-/g, "_") : "SCHOOL_VIEW";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [timeRange, setTimeRange] = useState<"Weekly" | "Monthly">("Weekly");
  const { data: userInfo } = useCurrentUser();
  const schoolHash = useMemo(() => {
    return userInfo?.schoolHash ?? null;
  }, [userInfo?.schoolHash]);

  const { data: schoolData } = useSchoolHierarchy({
    schoolHash: schoolHash,
    enabled: !!schoolHash,
  });

  const [selectedClassId, setSelectedClassId] = useState<string>("");
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");

  const apiPayload = useMemo(() => {
    return {
      schoolHash,
      grade: selectedClassId || undefined,
      studentId: selectedStudentId || undefined,
      range: timeRange === "Monthly" ? "monthly" : "weekly",
    };
  }, [schoolHash, selectedClassId, selectedStudentId, timeRange]);

  const classes = useMemo(() => {
    return schoolData?.hierarchy || [];
  }, [schoolData?.hierarchy]);

  useEffect(() => {
    if (classes.length > 0 && !selectedClassId) {
      setSelectedClassId(String(classes[0].classId));
    }
  }, [classes, selectedClassId]);

  const selectedClassObj = classes.find(
    (c: any) => String(c.classId) === String(selectedClassId)
  );
  const students = selectedClassObj?.students || [];

  useEffect(() => {
    if (students.length > 0) {
      setSelectedStudentId(String(students[0].id));
    } else {
      setSelectedStudentId("");
    }
  }, [selectedClassId, students]);

  const selectedStudentObj = useMemo(() => {
    return students?.find(
      (stu: any) => String(stu?.id) === String(selectedStudentId)
    );
  }, [selectedStudentId, students]);

  const selectedStudentName = selectedStudentObj?.name || "";

  useEffect(() => {
    if (students.length > 0 && !selectedStudentId) {
      setSelectedStudentId(String(students[0].id));
    }
  }, [students, selectedStudentId]);

  return (
    <div className="admin-root">
      <SuperAdminSidebar
        activeTab={tab}
        onChange={() => {}}
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((c) => !c)}
      />
      <div className="admin-right">
        <Header
          isAdmin={tab !== "SCHOOL_VIEW"}
          grade={selectedClassId}
          grades={classes}
          onGradeChange={setSelectedClassId}
        />
        <AdminSubHeader
          tab={tab}
          selectedStudent={selectedStudentObj || null}
          onStudentChange={(student: any) =>
            setSelectedStudentId(String(student?.id || ""))
          }
          timeRange={timeRange}
          onTimeChange={setTimeRange}
          search=""
          onSearch={() => {}}
          students={students}
        />
        <main className="dashboard">
          {tab === "SCHOOL_VIEW" && <SchoolView data={apiPayload} />}
          {tab === "CLASSROOM_VIEW" && (
            <ClassRoomView dashboardData={apiPayload} />
          )}
          {tab === "STUDENT_VIEW" && (
            <StudentView name={selectedStudentName} data={apiPayload} />
          )}

          {tab === "ACTIVITIES" && (
              <Activities />
            )}
            {tab === "TROO_STORIES" && (
              <TrooStories
                schoolHash={schoolData?.schoolHash || ""}
                teacherId={userInfo?.userName || ""}
              />
            )}
        </main>
      </div>
    </div>
  );
};

export default SuperAdmin;
